/**
 * Client OS, Platform, and Architecture Detection Utility
 * Synchronous multi-heuristic detection with WebGL Apple Silicon detection
 */

export function detectUserEnvironmentSync() {
  if (typeof window === 'undefined') {
    return {
      os: 'windows',
      arch: 'x64',
      name: 'Windows',
      detail: 'Windows (64-bit)',
      browser: 'Browser',
      isMobile: false,
      isAutoDetected: false,
    };
  }

  const nav = window.navigator || {};
  const ua = nav.userAgent || '';
  const platform = nav.platform || '';
  const uadPlatform = nav.userAgentData?.platform || '';

  // Detect browser name for informative UI
  let browser = 'Browser';
  if (/Edg\//i.test(ua)) browser = 'Microsoft Edge';
  else if (/Chrome\//i.test(ua) && !/Edg\//i.test(ua)) browser = 'Google Chrome';
  else if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua)) browser = 'Apple Safari';
  else if (/Firefox\//i.test(ua)) browser = 'Mozilla Firefox';
  else if (/Brave/i.test(ua)) browser = 'Brave';

  // 1. Mobile Check (iOS / Android)
  const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(ua) || nav.userAgentData?.mobile;
  if (isMobile) {
    if (/iPhone|iPad|iPod/i.test(ua)) {
      return {
        os: 'macos',
        arch: 'ARM64',
        name: 'iOS / iPadOS',
        detail: 'Apple Mobile Device',
        browser,
        isMobile: true,
        isAutoDetected: true,
      };
    }
    return {
      os: 'windows',
      arch: 'x64',
      name: 'Android',
      detail: 'Android Mobile Device',
      browser,
      isMobile: true,
      isAutoDetected: true,
    };
  }

  // 2. macOS Detection
  if (
    /Macintosh|MacIntel|MacPPC|Mac68K/i.test(platform) ||
    /Mac OS X/i.test(ua) ||
    /macOS/i.test(uadPlatform)
  ) {
    let isAppleSilicon = false;
    // Check WebGL GPU renderer for Apple Silicon (M1/M2/M3/M4 or Apple GPU)
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      if (gl) {
        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        if (debugInfo) {
          const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || '';
          if (/Apple/i.test(renderer) && !/Intel/i.test(renderer)) {
            isAppleSilicon = true;
          }
        }
      }
    } catch (_) {}

    // Fallback Apple Silicon check
    if (!isAppleSilicon && (nav.maxTouchPoints > 0 || /ARM/i.test(ua))) {
      isAppleSilicon = true;
    }

    return {
      os: 'macos',
      arch: isAppleSilicon ? 'ARM64' : 'x64',
      name: 'macOS',
      detail: isAppleSilicon ? 'macOS (Apple Silicon M-Series)' : 'macOS (Intel 64-bit)',
      browser,
      isMobile: false,
      isAutoDetected: true,
    };
  }

  // 3. Linux Detection
  if (/Linux/i.test(platform) || /Linux/i.test(ua) || /Linux/i.test(uadPlatform)) {
    let linuxDetail = 'Linux (x86_64)';
    let isRpm = false;
    if (/Fedora/i.test(ua)) {
      linuxDetail = 'Fedora Linux (.rpm 64-bit)';
      isRpm = true;
    } else if (/Red Hat|RHEL|CentOS/i.test(ua)) {
      linuxDetail = 'RHEL / CentOS (.rpm 64-bit)';
      isRpm = true;
    } else if (/SUSE|openSUSE/i.test(ua)) {
      linuxDetail = 'openSUSE (.rpm 64-bit)';
      isRpm = true;
    } else if (/Ubuntu/i.test(ua)) {
      linuxDetail = 'Ubuntu Linux (.deb 64-bit)';
    } else if (/Debian/i.test(ua)) {
      linuxDetail = 'Debian Linux (.deb 64-bit)';
    } else if (/Arch/i.test(ua)) {
      linuxDetail = 'Arch Linux (64-bit)';
    }

    return {
      os: 'linux',
      arch: 'x64',
      name: 'Linux',
      detail: linuxDetail,
      packageFormat: isRpm ? '.rpm' : '.deb',
      browser,
      isMobile: false,
      isAutoDetected: true,
    };
  }

  // 4. Windows Detection (Default)
  const isArm = /ARM64|ARM/i.test(ua);
  const isWin11or10 = /Windows NT 10.0/i.test(ua);
  const detail = isArm
    ? 'Windows 11 on ARM'
    : isWin11or10
    ? 'Windows 10 / 11 (64-bit)'
    : 'Windows (64-bit)';

  return {
    os: 'windows',
    arch: isArm ? 'ARM64' : 'x64',
    name: 'Windows',
    detail,
    browser,
    isMobile: false,
    isAutoDetected: true,
  };
}
